import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight, ShoppingCart } from 'lucide-react';
import NavBar from '../components/NavBar';

const CartPage = () => {
  const { cartItems, removeFromCart, updateQuantity, getTotal } = useCart();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState('cart');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (currentPage === 'home') {
      navigate('/');
    } else if (currentPage === 'profile') {
      navigate('/profile');
    }
  }, [currentPage, navigate]);

  const handleCheckout = () => {
    if (!currentUser) {
      navigate('/login');
    } else {
      navigate('/checkout');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <NavBar 
        currentPage={currentPage} 
        setCurrentPage={setCurrentPage} 
        mobileMenuOpen={mobileMenuOpen} 
        setMobileMenuOpen={setMobileMenuOpen} 
      />
      
      <div className="pt-24 pb-12 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <button 
              onClick={() => navigate(-1)} 
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
            >
              <ArrowRight className="w-5 h-5" />
              العودة
            </button>
            <div className="flex items-center gap-3 mb-2">
              <ShoppingCart className="w-8 h-8 text-yellow-600" />
              <h1 className="text-4xl font-bold text-gray-900">سلة التسوق</h1>
            </div>
            <p className="text-gray-600">
              {cartItems.length > 0 
                ? `لديك ${cartItems.length} ${cartItems.length === 1 ? 'منتج' : 'منتجات'} في السلة`
                : 'سلة التسوق فارغة'
              }
            </p>
          </div>

          {cartItems.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
              <ShoppingBag className="w-24 h-24 mx-auto text-gray-300 mb-6" />
              <h2 className="text-2xl font-bold text-gray-900 mb-4">سلة التسوق فارغة</h2>
              <p className="text-gray-600 mb-8">لم تقم بإضافة أي منتجات بعد</p>
              <button
                onClick={() => navigate('/')}
                className="bg-yellow-500 hover:bg-yellow-600 text-white px-8 py-4 rounded-xl font-bold transition-colors inline-flex items-center gap-2"
              >
                <ShoppingBag className="w-5 h-5" />
                تصفح المنتجات
              </button>
            </div>
          ) : (
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Cart Items */}
              <div className="lg:col-span-2 space-y-4">
                {cartItems.map((item) => {
                  const priceString = item.price || '0';
                  const price = parseFloat(String(priceString).replace('$', '').replace(',', '')) || 0;
                  const subtotal = price * (item.quantity || 1);

                  return (
                    <div key={item.id} className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow overflow-hidden">
                      <div className="p-6">
                        <div className="flex flex-col sm:flex-row gap-6">
                          {/* Product Image */}
                          <div className="flex-shrink-0">
                            <div className="w-24 h-24 sm:w-32 sm:h-32 bg-gradient-to-br from-yellow-100 to-orange-100 rounded-xl flex items-center justify-center text-6xl">
                              {String(item.image || '📦')}
                            </div>
                          </div>

                          {/* Product Info */}
                          <div className="flex-1">
                            <div className="flex justify-between items-start mb-3">
                              <div>
                                <h3 className="text-xl font-bold text-gray-900 mb-1">
                                  {String(item.name || '')}
                                </h3>
                                {item.categoryName && (
                                  <p className="text-sm text-gray-500 mb-2">
                                    {String(item.categoryName)}
                                  </p>
                                )}
                                {item.brand && (
                                  <p className="text-sm text-gray-600">
                                    العلامة: {String(item.brand)}
                                  </p>
                                )}
                              </div>
                              <button
                                onClick={() => removeFromCart(item.id)}
                                className="text-red-500 hover:text-red-700 p-2 hover:bg-red-50 rounded-lg transition-colors"
                                title="حذف من السلة"
                              >
                                <Trash2 className="w-5 h-5" />
                              </button>
                            </div>

                            {/* Price and Quantity */}
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mt-4 pt-4 border-t border-gray-100">
                              <div className="flex items-center gap-3">
                                <span className="text-sm text-gray-600">الكمية:</span>
                                <div className="flex items-center gap-2 bg-gray-50 rounded-lg border border-gray-200">
                                  <button
                                    onClick={() => updateQuantity(item.id, item.quantity - 1)}
                                    className="p-2 hover:bg-gray-200 rounded-r-lg transition-colors"
                                    disabled={item.quantity <= 1}
                                  >
                                    <Minus className="w-4 h-4" />
                                  </button>
                                  <span className="px-4 font-bold text-lg min-w-[40px] text-center">
                                    {item.quantity}
                                  </span>
                                  <button
                                    onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                    className="p-2 hover:bg-gray-200 rounded-l-lg transition-colors"
                                  >
                                    <Plus className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>

                              <div className="flex items-center justify-between sm:justify-end gap-4">
                                <div className="text-right">
                                  <p className="text-sm text-gray-600">السعر للواحد</p>
                                  <p className="text-lg font-bold text-gray-900">
                                    ${price.toFixed(2)}
                                  </p>
                                </div>
                                <div className="text-right">
                                  <p className="text-sm text-gray-600">المجموع</p>
                                  <p className="text-xl font-bold text-yellow-600">
                                    ${subtotal.toFixed(2)}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Order Summary */}
              <div className="lg:col-span-1">
                <div className="bg-white rounded-xl shadow-lg p-6 sticky top-24">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">ملخص الطلب</h2>
                  
                  <div className="space-y-4 mb-6">
                    <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                      <span className="text-gray-600">عدد المنتجات</span>
                      <span className="font-bold text-gray-900">{cartItems.length}</span>
                    </div>
                    
                    <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                      <span className="text-gray-600">المجموع الفرعي</span>
                      <span className="font-bold text-gray-900">${getTotal().toFixed(2)}</span>
                    </div>
                    
                    <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                      <span className="text-gray-600">الشحن</span>
                      <span className="font-bold text-green-600">مجاني</span>
                    </div>
                    
                    <div className="flex justify-between items-center pt-2">
                      <span className="text-lg font-bold text-gray-900">الإجمالي</span>
                      <span className="text-2xl font-bold text-yellow-600">
                        ${getTotal().toFixed(2)}
                      </span>
                    </div>
                  </div>

                  <button
                    onClick={handleCheckout}
                    className="w-full bg-gradient-to-r from-yellow-500 to-orange-600 hover:from-yellow-600 hover:to-orange-700 text-white font-bold py-4 rounded-xl transition-all shadow-lg text-lg flex items-center justify-center gap-2"
                  >
                    <ShoppingCart className="w-5 h-5" />
                    إتمام الطلب
                  </button>

                  {!currentUser && (
                    <p className="text-center text-sm text-gray-600 mt-4">
                      يجب تسجيل الدخول لإتمام الطلب
                    </p>
                  )}

                  <button
                    onClick={() => navigate('/')}
                    className="w-full mt-4 bg-gray-100 hover:bg-gray-200 text-gray-900 font-medium py-3 rounded-xl transition-colors"
                  >
                    متابعة التسوق
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CartPage;
